<template>
  <div class="people-admin">
    <div v-if="loading" class="people-loading">
      <b-spinner small /> {{ $t('people_loading') }}
    </div>

    <b-alert v-else-if="error" show variant="danger">{{ error }}</b-alert>

    <template v-else-if="status">
      <b-alert v-if="operationError" show dismissible variant="danger" @dismissed="operationError = null">
        {{ operationError }}
      </b-alert>

      <b-alert v-if="status.security && status.security.unprotected" show variant="warning">
        <strong>{{ $t('people_security_warning_title') }}</strong>
        <div>{{ $t('people_security_warning') }}</div>
      </b-alert>

      <b-alert v-if="status.capability.state === 'unavailable'" show variant="warning">
        <strong>{{ $t('people_unavailable') }}</strong>
        <div>{{ status.capability.reason }}</div>
        <small>{{ $t('people_core_safe') }}</small>
      </b-alert>

      <div v-else-if="!status.jobs.enabled" class="people-onboarding">
        <div class="people-onboarding-icon">
          <i class="bi bi-people-fill" />
        </div>
        <h3>{{ $t('people_title') }}</h3>
        <p>{{ $t('people_onboarding') }}</p>
        <p class="privacy-note">
          <i class="bi bi-shield-lock" /> {{ $t('people_privacy') }}
        </p>
        <b-button variant="primary" size="lg" @click="enable">
          {{ $t('people_enable') }}
        </b-button>
      </div>

      <template v-else>
        <PeopleDashboard
          :status="status"
          :last-action="lastAction"
          :active-filter="activeFilter"
          @analyze="analyze"
          @rebuild="confirmRebuild"
          @disable="confirmDisable"
          @erase="confirmErase"
          @pause="pause"
          @undo="undoLast"
          @select-filter="selectFilter"
          @open-failures="openFailures"
        />

        <PeopleGrid
          :active-filter="activeFilter"
          :candidate-filter="candidateFilter"
          :face-sort="faceSort"
          :curation-pending="pendingCurationOperations > 0"
          :items="items"
          :total="total"
          :selected-people="selectedPeople"
          :all-targets="allTargets"
          :confirmed-targets="confirmedTargets"
          :draft-targets="draftTargets"
          :review-targets="reviewTargets"
          :rematch="status.rematch"
          :face-rematch="status.face_rematch"
          @select-filter="selectFilter"
          @filter-candidates="filterCandidates"
          @sort-faces="sortFaces"
          @merge-selected="mergeSelected"
          @merge-into-confirmed="mergeIntoConfirmed"
          @hide-selected="hideSelected"
          @select-only-person="selectOnlyPerson"
          @toggle-person="togglePerson"
          @open-person="openPerson"
          @show-source="showSource"
          @set-review-target="setReviewTarget"
          @approve-review="approveReview"
          @ignore-review="ignoreReview"
          @assign-selected-reviews="assignSelectedReviews"
          @move-selected-to-unconfirmed="moveSelectedToUnconfirmed"
          @ignore-selected-reviews="ignoreSelectedReviews"
          @load-more="loadMore"
          @rematch="rematchCandidates"
          @cancel-rematch="cancelRematch"
          @rematch-faces="rematchFaces"
          @cancel-face-rematch="cancelFaceRematch"
        />
      </template>
    </template>

    <PersonDetailDialog
      ref="detailDialog"
      v-model="detailOpen"
      :detail="detail"
      :edit-name="editName"
      :selected-faces="selectedFaces"
      :loading-faces="detailLoadingFaces"
      :move-target="moveTarget"
      :move-targets="moveTargets"
      :video-job="status && status.aging_video"
      :video-download-url="agingVideoDownloadUrl"
      @update:edit-name="editName = $event"
      @update:move-target="moveTarget = $event"
      @confirm="confirmPerson"
      @save="savePerson"
      @make-draft="makeDraft"
      @toggle-hidden="toggleHidden"
      @merge-into-person="mergeDetailIntoPerson"
      @toggle-face="toggleFace"
      @move-faces="moveSelectedFaces"
      @split-faces="splitSelected"
      @ignore-faces="ignoreSelected"
      @show-source="showSource"
      @load-more="loadMoreFaces"
      @generate-video="generateAgingVideo"
      @cancel-video="cancelAgingVideo"
    />

    <FaceSourceDialog v-model="sourceOpen" :face="sourceFace" />

    <DraftNameDialog ref="draftNameDialog" />

    <PeopleFailuresDialog
      v-model="failureOpen"
      :loading="failureLoading"
      :failures="failures"
      @load-more="loadFailures(true)"
      @retry="retryFailures"
    />
  </div>
</template>

<script>
import People from '../../js/model/people.js'
import DraftNameDialog from './people/DraftNameDialog.vue'
import FaceSourceDialog from './people/FaceSourceDialog.vue'
import PeopleDashboard from './people/PeopleDashboard.vue'
import PeopleFailuresDialog from './people/PeopleFailuresDialog.vue'
import PeopleGrid from './people/PeopleGrid.vue'
import PersonDetailDialog from './people/PersonDetailDialog.vue'

export default {
  components: {
    DraftNameDialog,
    FaceSourceDialog,
    PeopleDashboard,
    PeopleFailuresDialog,
    PeopleGrid,
    PersonDetailDialog,
  },
  data() {
    return {
      model: null,
      loading: true,
      error: null,
      operationError: null,
      status: null,
      activeFilter: 'review',
      candidateFilter: '',
      faceSort: 'newest',
      items: [],
      total: 0,
      limit: 50,
      itemWindows: {
        review: 50,
        candidate: 50,
        confirmed: 50,
        hidden: 50,
        ungrouped: 50,
      },
      listRevision: 0,
      pendingCurationOperations: 0,
      curationContextDirty: false,
      curationTargetsDirty: false,
      selectedPeople: [],
      actions: [],
      allTargets: [],
      reviewTargets: {},
      detailOpen: false,
      detail: null,
      detailLoadingFaces: false,
      editName: '',
      selectedFaces: [],
      moveTarget: null,
      sourceOpen: false,
      sourceFace: null,
      failureOpen: false,
      failureLoading: false,
      failures: { total: 0, items: [] },
      poller: null,
    }
  },
  computed: {
    selectedPeopleObjects() {
      return this.items.filter(item => this.selectedPeople.includes(item.id))
    },
    agingVideoDownloadUrl() {
      const job = this.status?.aging_video
      return job?.status === 'completed' && job.download_ready
        ? this.model.agingVideoDownloadUrl(job.id)
        : ''
    },
    moveTargets() {
      const statusOrder = { confirmed: 0, candidate: 1 }
      return this.allTargets
        .filter(item => item.id !== this.detail?.id && item.name?.trim() && item.status in statusOrder)
        .sort((first, second) => statusOrder[first.status] - statusOrder[second.status]
          || first.name.localeCompare(second.name))
    },
    confirmedTargets() {
      return this.allTargets
        .filter(item => item.status === 'confirmed')
        .sort((first, second) => first.name.localeCompare(second.name))
    },
    draftTargets() {
      return this.allTargets
        .filter(item => item.status === 'candidate' && item.name?.trim())
        .sort((first, second) => first.name.localeCompare(second.name))
    },
    lastAction() {
      return this.actions.find(action => action.undone_at == null)
    },
  },
  created() {
    this.model = new People({ http: this.$http })
    this.refresh()
    this.poller = setInterval(() => {
      if (
        this.status?.jobs?.pending ||
        this.status?.jobs?.running ||
        ['pending', 'running'].includes(this.status?.rematch?.status)
        || ['pending', 'running'].includes(this.status?.face_rematch?.status)
        || ['pending', 'running'].includes(this.status?.aging_video?.status)
      ) this.refreshStatus()
    }, 3000)
  },
  beforeDestroy() {
    clearInterval(this.poller)
  },
  methods: {
    errorMessage(error) {
      return error.response?.data?.error || error.message || String(error)
    },
    async runOperation(operation) {
      this.operationError = null
      try {
        await operation()
        return true
      } catch (error) {
        this.operationError = this.errorMessage(error)
        return false
      }
    },
    async refresh() {
      this.loading = true
      this.error = null
      try {
        await this.refreshLoadedData()
      } catch (error) {
        this.error = this.errorMessage(error)
      } finally {
        this.loading = false
      }
    },
    async refreshLoadedData() {
      await this.refreshStatus()
      if (this.status.jobs.enabled && this.status.capability.state === 'ready') {
        await Promise.all([this.loadItems(), this.loadActions(), this.loadTargets()])
      }
    },
    async refreshStatus() {
      const previousRematch = this.status?.rematch
      const previousFaceRematch = this.status?.face_rematch
      const nextStatus = await this.model.status()
      this.status = nextStatus
      const wasActive = ['pending', 'running'].includes(previousRematch?.status)
      const isActive = ['pending', 'running'].includes(nextStatus.rematch?.status)
      if (wasActive && !isActive && previousRematch.id === nextStatus.rematch?.id) {
        await this.applyRematchResult(nextStatus.rematch)
      }
      const faceWasActive = ['pending', 'running'].includes(previousFaceRematch?.status)
      const faceIsActive = ['pending', 'running'].includes(nextStatus.face_rematch?.status)
      if (faceWasActive && !faceIsActive && previousFaceRematch.id === nextStatus.face_rematch?.id) {
        await this.applyFaceRematchResult()
      }
    },
    async loadActions() {
      this.actions = await this.model.actions()
    },
    async enable() {
      await this.runOperation(async () => {
        await this.model.enable(true)
        await this.refresh()
        await this.analyze('all')
      })
    },
    async pause(paused) {
      await this.runOperation(async () => {
        await this.model.pause(paused)
        await this.refreshStatus()
      })
    },
    async analyze(operation) {
      await this.runOperation(async () => {
        await this.model.analyze(operation)
        await this.refresh()
      })
    },
    async confirmRebuild() {
      const confirmed = await this.$bvModal.msgBoxConfirm(this.$t('people_rebuild_confirm'), { okVariant: 'danger' })
      if (confirmed) await this.analyze('rebuild')
    },
    async confirmErase() {
      const confirmed = await this.$bvModal.msgBoxConfirm(this.$t('people_erase_confirm'), {
        okTitle: this.$t('people_erase'),
        okVariant: 'danger',
      })
      if (!confirmed) return
      await this.runOperation(async () => {
        await this.model.erase()
        this.items = []
        this.allTargets = []
        await this.refresh()
      })
    },
    async confirmDisable() {
      const confirmed = await this.$bvModal.msgBoxConfirm(this.$t('people_disable_confirm'))
      if (!confirmed) return
      await this.runOperation(async () => {
        await this.model.enable(false)
        await this.refresh()
      })
    },
    async selectFilter(filter) {
      this.activeFilter = filter
      this.selectedPeople = []
      await this.loadItems()
    },
    async filterCandidates(value) {
      this.candidateFilter = value
      this.selectedPeople = []
      await this.loadItems()
    },
    async sortFaces(value) {
      this.faceSort = value
      this.selectedPeople = []
      this.$set(this.itemWindows, this.activeFilter, this.limit)
      await this.loadItems()
    },
    async loadItems(append=false) {
      const filter = this.activeFilter
      const candidateFilter = this.candidateFilter.trim()
      const faceSort = this.faceSort
      const revision = this.listRevision
      const itemWindow = this.itemWindows[filter] ?? this.limit
      const loadPage = (offset, limit) => filter === 'review'
        ? this.model.faces('review', offset, limit, faceSort)
        : filter === 'ungrouped'
          ? this.model.unassignedFaces(offset, limit, faceSort)
          : filter === 'candidate'
            ? candidateFilter
              ? this.model.persons(filter, offset, limit, candidateFilter)
              : this.model.persons(filter, offset, limit)
            : this.model.persons(filter, offset, limit)
      const isCurrent = () => filter === this.activeFilter
        && (filter !== 'candidate' || candidateFilter === this.candidateFilter.trim())
        && (!['review', 'ungrouped'].includes(filter) || faceSort === this.faceSort)
        && revision === this.listRevision

      if (append) {
        const data = await loadPage(this.items.length, this.limit)
        if (!isCurrent()) return
        this.items.push(...data.items)
        this.total = data.total
        this.$set(this.itemWindows, filter, Math.max(itemWindow, this.items.length))
        if (filter === 'review' || filter === 'ungrouped') {
          for (const face of data.items) this.$set(this.reviewTargets, face.id, face.person_id || null)
        }
        return
      }

      const items = []
      let total = 0
      do {
        const requestLimit = Math.min(200, itemWindow - items.length)
        const data = await loadPage(items.length, requestLimit)
        if (!isCurrent()) return
        total = data.total
        items.push(...data.items)
        if (filter === 'review' || filter === 'ungrouped') {
          for (const face of data.items) this.$set(this.reviewTargets, face.id, face.person_id || null)
        }
        if (data.items.length === 0) break
      } while (items.length < Math.min(itemWindow, total))
      this.items = items
      this.total = total
    },
    async loadTargets() {
      const loadStatus = async status => {
        const items = []
        let offset = 0
        let total = 0
        do {
          const page = await this.model.persons(status, offset, 200)
          total = page.total
          items.push(...page.items)
          offset = items.length
        } while (offset < total)
        return items
      }
      const [confirmed, candidates] = await Promise.all([loadStatus('confirmed'), loadStatus('candidate')])
      this.allTargets = [
        ...confirmed.sort((first, second) => first.name.localeCompare(second.name)),
        ...candidates
          .filter(person => person.name?.trim())
          .sort((first, second) => first.name.localeCompare(second.name)),
      ]
    },
    loadMore() {
      if (this.pendingCurationOperations > 0) return
      return this.loadItems(true)
    },
    async rematchCandidates() {
      if (this.total === 0) return
      const confirmed = await this.$bvModal.msgBoxConfirm(this.$tc(
        'people_rematch_confirm',
        this.total,
        { count: this.total },
      ))
      if (!confirmed) return
      await this.runOperation(async () => {
        this.status.rematch = await this.model.rematch()
      })
    },
    async cancelRematch() {
      const jobId = this.status?.rematch?.id
      if (!jobId) return
      await this.runOperation(async () => {
        this.status.rematch = await this.model.cancelRematch(jobId)
      })
    },
    async rematchFaces() {
      if (this.total === 0) return
      const confirmed = await this.$bvModal.msgBoxConfirm(this.$tc(
        'people_face_rematch_confirm',
        this.total,
        { count: this.total },
      ))
      if (!confirmed) return
      await this.runOperation(async () => {
        this.status.face_rematch = await this.model.rematchFaces()
      })
    },
    async cancelFaceRematch() {
      const jobId = this.status?.face_rematch?.id
      if (!jobId) return
      await this.runOperation(async () => {
        this.status.face_rematch = await this.model.cancelFaceRematch(jobId)
      })
    },
    async generateAgingVideo(options) {
      if (!this.detail) return
      await this.runOperation(async () => {
        const job = await this.model.generateAgingVideo(this.detail.id, options)
        this.$set(this.status, 'aging_video', job)
      })
    },
    async cancelAgingVideo() {
      const jobId = this.status?.aging_video?.id
      if (!jobId) return
      await this.runOperation(async () => {
        const job = await this.model.cancelAgingVideo(jobId)
        this.$set(this.status, 'aging_video', job)
      })
    },
    async applyRematchResult(rematch) {
      const matchedIds = new Set(rematch.matched_source_ids || [])
      if (this.activeFilter === 'candidate' && matchedIds.size > 0) {
        if (this.candidateFilter.trim()) {
          await this.loadItems()
        } else {
          for (let index = this.items.length - 1; index >= 0; index--) {
            if (matchedIds.has(this.items[index].id)) this.items.splice(index, 1)
          }
          this.total = this.status.people.candidates
        }
      }
      this.selectedPeople = this.selectedPeople.filter(id => !matchedIds.has(id))
      await Promise.all([this.loadTargets(), this.loadActions()])
    },
    async applyFaceRematchResult() {
      if (this.activeFilter === 'ungrouped' || this.activeFilter === 'review') await this.loadItems()
      await this.loadTargets()
    },
    setReviewTarget(faceId, targetId) {
      this.$set(this.reviewTargets, faceId, targetId)
    },
    togglePerson(id) {
      this.selectedPeople = this.selectedPeople.includes(id)
        ? this.selectedPeople.filter(value => value !== id)
        : [...this.selectedPeople, id]
    },
    selectOnlyPerson(id) {
      this.selectedPeople = [id]
    },
    async openPerson(person) {
      this.detailLoadingFaces = false
      this.detail = await this.model.person(person.id)
      this.editName = this.detail.name
      this.selectedFaces = []
      this.moveTarget = null
      this.detailOpen = true
    },
    toggleFace(id) {
      this.selectedFaces = this.selectedFaces.includes(id)
        ? this.selectedFaces.filter(value => value !== id)
        : [...this.selectedFaces, id]
    },
    updateDetailPerson(person) {
      const faces = this.detail.faces
      Object.assign(this.detail, person)
      this.detail.faces = faces
      this.editName = this.detail.name
    },
    removeDetailFaces(faceIds) {
      const removedIds = new Set(faceIds)
      const items = this.detail.faces.items
      for (let index = items.length - 1; index >= 0; index--) {
        if (removedIds.has(items[index].id)) items.splice(index, 1)
      }
      this.detail.faces.total = Math.max(0, this.detail.faces.total - removedIds.size)
      this.detail.face_count = this.detail.faces.total
      this.selectedFaces = []
      this.moveTarget = null
      this.$nextTick(() => this.$refs.detailDialog?.maybeLoadMore?.())
    },
    async refreshDetailContext() {
      await Promise.all([this.refreshStatus(), this.loadItems(), this.loadTargets(), this.loadActions()])
    },
    async loadMoreFaces() {
      if (this.detailLoadingFaces || !this.detail || this.detail.faces.items.length >= this.detail.faces.total) return
      const personId = this.detail.id
      const offset = this.detail.faces.items.length
      this.operationError = null
      this.detailLoadingFaces = true
      try {
        const next = await this.model.person(personId, offset, 100)
        if (this.detail?.id !== personId) return
        this.detail.faces.items.push(...next.faces.items)
        this.detail.faces.total = next.faces.total
      } catch (error) {
        this.operationError = this.errorMessage(error)
      } finally {
        this.detailLoadingFaces = false
      }
    },
    async confirmPerson() {
      await this.runOperation(async () => {
        const person = await this.model.confirm(this.detail, this.editName)
        this.updateDetailPerson(person)
        await this.refreshDetailContext()
      })
    },
    async savePerson() {
      await this.runOperation(async () => {
        const person = await this.model.update(this.detail, { name: this.editName })
        this.updateDetailPerson(person)
        await this.refreshDetailContext()
      })
    },
    async makeDraft() {
      await this.runOperation(async () => {
        const person = await this.model.update(this.detail, { draft: true })
        this.updateDetailPerson(person)
        await this.refreshDetailContext()
      })
    },
    async toggleHidden() {
      await this.runOperation(async () => {
        const person = await this.model.update(this.detail, { hidden: this.detail.status !== 'hidden' })
        this.updateDetailPerson(person)
        await this.refreshDetailContext()
      })
    },
    async moveSelectedFaces() {
      const target = this.moveTarget == null ? null : this.moveTargets.find(person => person.id === this.moveTarget)
      await this.runOperation(async () => {
        const movedFaceIds = [...this.selectedFaces]
        await this.model.move(movedFaceIds, target)
        this.removeDetailFaces(movedFaceIds)
        await this.refreshDetailContext()
      })
    },
    async ignoreSelected() {
      await this.runOperation(async () => {
        const ignoredFaceIds = [...this.selectedFaces]
        await this.model.ignore(ignoredFaceIds)
        this.removeDetailFaces(ignoredFaceIds)
        await this.refreshDetailContext()
      })
    },
    async splitSelected() {
      await this.runOperation(async () => {
        const splitFaceIds = [...this.selectedFaces]
        await this.model.split(this.detail, splitFaceIds)
        this.removeDetailFaces(splitFaceIds)
        await this.refreshDetailContext()
      })
    },
    async mergeSelected() {
      const people = this.selectedPeopleObjects
      if (people.length < 2) return
      const hasConfirmed = people.slice(1).some(person => person.status === 'confirmed')
      const confirmed = await this.$bvModal.msgBoxConfirm(this.$tc(
        hasConfirmed ? 'people_merge_confirmed' : 'people_merge_confirm',
        people.length,
        { count: people.length },
      ), { okVariant: 'danger' })
      if (!confirmed) return
      await this.runOperation(async () => {
        await this.model.merge(people[0], people.slice(1), hasConfirmed)
        this.selectedPeople = []
        await this.refreshLoadedData()
      })
    },
    async mergeIntoConfirmed(targetId) {
      const sources = this.selectedPeopleObjects
      const target = this.confirmedTargets.find(person => person.id === targetId)
      if (sources.length === 0 || !target) return
      await this.mergeSourcesIntoTarget(target, sources, () => {
        this.selectedPeople = []
      })
    },
    async mergeDetailIntoPerson(targetId) {
      const source = this.detail
      const target = this.moveTargets.find(person => person.id === targetId)
      if (!source || source.status !== 'candidate' || !target) return
      await this.mergeSourcesIntoTarget(target, [source], () => {
        this.detailOpen = false
        this.detail = null
        this.selectedFaces = []
      })
    },
    async mergeSourcesIntoTarget(target, sources, onMerged) {
      const confirmed = await this.$bvModal.msgBoxConfirm(this.$tc('people_merge_into_confirm', sources.length, {
        count: sources.length,
        name: target.name,
      }), { okVariant: 'danger' })
      if (!confirmed) return
      await this.runOperation(async () => {
        await this.model.merge(target, sources, false)
        onMerged()
        await this.refreshLoadedData()
      })
    },
    async hideSelected() {
      const people = this.selectedPeopleObjects
      if (people.length === 0) return
      const confirmed = await this.$bvModal.msgBoxConfirm(this.$tc('people_hide_confirm', people.length, {
        count: people.length,
      }), { okVariant: 'danger' })
      if (!confirmed) return
      await this.runOperation(async () => {
        await this.model.hide(people)
        this.selectedPeople = []
        await this.refreshLoadedData()
      })
    },
    async approveReview(face) {
      await this.assignReviewFaces([face.id], this.reviewTargets[face.id])
    },
    async ignoreReview(face) {
      await this.optimisticReviewOperation([face.id], () => this.model.ignore([face.id]))
    },
    async assignSelectedReviews(targetId) {
      await this.assignReviewFaces([...this.selectedPeople], targetId)
    },
    async moveSelectedToUnconfirmed() {
      const faceIds = [...this.selectedPeople]
      await this.optimisticReviewOperation(faceIds, () => this.model.createUnconfirmed(faceIds))
    },
    async ignoreSelectedReviews() {
      const faceIds = [...this.selectedPeople]
      await this.optimisticReviewOperation(faceIds, () => this.model.ignore(faceIds))
    },
    async assignReviewFaces(faceIds, targetId) {
      if (targetId === 'create-draft') {
        const name = await this.$refs.draftNameDialog.open()
        if (!name) return
        await this.optimisticReviewOperation(
          faceIds,
          () => this.model.createDraft(faceIds, name),
          { refreshTargets: true },
        )
        return
      }
      const target = this.allTargets.find(person => person.id === targetId)
      if (!target) return
      await this.optimisticReviewOperation(faceIds, () => this.model.move(faceIds, target))
    },
    async optimisticReviewOperation(faceIds, operation, { refreshTargets=false }={}) {
      const ids = new Set(faceIds)
      if (ids.size === 0) return
      const filter = this.activeFilter
      const removedItems = this.items
        .map((item, index) => ({ item, index }))
        .filter(({ item }) => ids.has(item.id))
      const removedSelection = this.selectedPeople.filter(id => ids.has(id))
      this.listRevision += 1
      this.pendingCurationOperations += 1
      this.items = this.items.filter(item => !ids.has(item.id))
      this.total = Math.max(0, this.total - ids.size)
      this.selectedPeople = this.selectedPeople.filter(id => !ids.has(id))
      this.operationError = null
      try {
        await operation()
        this.curationContextDirty = true
        if (refreshTargets) this.curationTargetsDirty = true
      } catch (error) {
        if (this.activeFilter === filter) {
          const currentIds = new Set(this.items.map(item => item.id))
          let restored = 0
          for (const { item, index } of removedItems) {
            if (currentIds.has(item.id)) continue
            this.items.splice(Math.min(index, this.items.length), 0, item)
            currentIds.add(item.id)
            restored += 1
          }
          this.total += restored
          this.selectedPeople = [...new Set([...this.selectedPeople, ...removedSelection])]
        }
        this.operationError = this.errorMessage(error)
      } finally {
        this.pendingCurationOperations = Math.max(0, this.pendingCurationOperations - 1)
        if (this.pendingCurationOperations === 0 && this.curationContextDirty) {
          const shouldRefreshTargets = this.curationTargetsDirty
          this.curationContextDirty = false
          this.curationTargetsDirty = false
          try {
            const refreshes = [this.refreshStatus(), this.loadActions()]
            if (shouldRefreshTargets) refreshes.push(this.loadTargets())
            await Promise.all(refreshes)
          } catch (error) {
            this.operationError = this.errorMessage(error)
          }
        }
      }
    },
    async undoLast() {
      if (!this.lastAction) return
      await this.runOperation(async () => {
        await this.model.undo(this.lastAction.id)
        await this.refreshLoadedData()
        if (this.detailOpen && this.detail) await this.reloadDetail()
      })
    },
    async openFailures() {
      this.failureOpen = true
      await this.loadFailures()
    },
    async loadFailures(append=false) {
      this.failureLoading = true
      try {
        const offset = append ? this.failures.items.length : 0
        const data = await this.model.failures(offset, 50)
        this.failures = {
          ...data,
          items: append ? [...this.failures.items, ...data.items] : data.items,
        }
      } finally {
        this.failureLoading = false
      }
    },
    async retryFailures() {
      await this.runOperation(async () => {
        await this.model.analyze('failed')
        this.failureOpen = false
        await this.refresh()
      })
    },
    showSource(face) {
      this.sourceFace = face
      this.sourceOpen = true
    },
  },
}
</script>

<style scoped>
.people-admin {
  min-height: 480px;
}

.people-loading {
  padding: 5rem 1rem;
  color: var(--ghosted-text-color);
  text-align: center;
}

.people-onboarding {
  max-width: 620px;
  margin: 4rem auto;
  padding: 3rem;
  border: 1px solid rgba(127, 127, 127, .25);
  border-radius: 18px;
  background: rgba(127, 127, 127, .06);
  text-align: center;
}

.people-onboarding-icon {
  display: grid;
  width: 84px;
  height: 84px;
  margin: 0 auto 1.5rem;
  border-radius: 50%;
  background: var(--highlight-color);
  color: white;
  font-size: 2.2rem;
  place-items: center;
}

.people-onboarding p {
  font-size: 1.05rem;
  line-height: 1.6;
}

.privacy-note {
  color: var(--ghosted-text-color);
}
</style>
